import React, { useState, useMemo } from 'react'
import { X, ChevronDown, ChevronRight, Gamepad2, Check, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useProjectsStore } from '../../store/useProjectsStore'
import { projectsService } from '../../services/projectsService'
import { FILL_GRADIENT } from '../../constants/gradients'

interface AddGameModalProps {
  isOpen: boolean
  onClose: () => void
  targetTeamId: string
  targetTeamName: string
  onGameAdded: () => void
}

interface GameGroup {
  teamId: string | null
  teamName: string
  isPersonal: boolean
  games: any[]
}

const AddGameModal: React.FC<AddGameModalProps> = ({
  isOpen,
  onClose,
  targetTeamId,
  targetTeamName,
  onGameAdded,
}) => {
  const { projects } = useProjectsStore()
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['personal']))
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null)
  const [isTransferring, setIsTransferring] = useState(false)

  const isPersonalTarget = targetTeamId === 'personal'

  const gameGroups = useMemo<GameGroup[]>(() => {
    const groupMap = new Map<string, GameGroup>()

    for (const project of projects) {
      // Skip archived games
      if (project.status === 'archived') continue

      const isPersonalProject =
        !project.team ||
        project.team?.is_personal === true ||
        project.team?.name === 'Personal' ||
        project.team_id === null ||
        project.team_id === undefined ||
        project.ownership_type === 'personal'

      if (isPersonalProject) {
        const key = '__personal__'
        if (!groupMap.has(key)) {
          groupMap.set(key, { teamId: null, teamName: 'Personal Workspace', isPersonal: true, games: [] })
        }
        groupMap.get(key)!.games.push(project)
      } else {
        const key = project.team?.id || project.team_id || '__unknown__'
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            teamId: key,
            teamName: project.team?.name || 'Unknown Team',
            isPersonal: false,
            games: [],
          })
        }
        groupMap.get(key)!.games.push(project)
      }
    }

    const groups = Array.from(groupMap.values()).filter(group => {
      if (isPersonalTarget) return !group.isPersonal
      return group.teamId !== targetTeamId
    })

    // Sort: personal first, then alphabetical
    groups.sort((a, b) => {
      if (a.isPersonal) return -1
      if (b.isPersonal) return 1
      return a.teamName.localeCompare(b.teamName)
    })

    return groups
  }, [projects])

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const getGroupKey = (group: GameGroup) => group.isPersonal ? '__personal__' : (group.teamId || '__unknown__')

  const isGameInTargetTeam = (project: any): boolean => {
    if (isPersonalTarget) {
      return (
        !project.team ||
        project.team?.is_personal === true ||
        project.team?.name === 'Personal' ||
        project.team_id === null ||
        project.team_id === undefined ||
        project.ownership_type === 'personal'
      )
    }
    return project.team?.id === targetTeamId || project.team_id === targetTeamId
  }

  const handleConfirm = async () => {
    if (!selectedGameId) return

    setIsTransferring(true)
    try {
      await projectsService.transferProject(selectedGameId, targetTeamId)
      toast.success('Game added to team')
      onGameAdded()
      onClose()
    } catch (error) {
      console.error('Failed to transfer game:', error)
      toast.error('Failed to add game to team')
    } finally {
      setIsTransferring(false)
    }
  }

  const handleClose = () => {
    if (isTransferring) return
    setSelectedGameId(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleClose} />
      <div className="relative bg-white dark:bg-dark-surface rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">Add Game</h2>
            <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-0.5">
              Select a game to add to <span className="font-semibold">{targetTeamName}</span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Game List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {gameGroups.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-dark-text-muted">
              <Gamepad2 className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p className="font-medium">No games available</p>
            </div>
          ) : (
            gameGroups.map(group => {
              const key = getGroupKey(group)
              const isExpanded = expandedGroups.has(key)
              const availableGames = group.games.filter(g => !isGameInTargetTeam(g))

              return (
                <div key={key} className="rounded-xl border border-gray-200 dark:border-dark-border overflow-hidden">
                  {/* Accordion Header */}
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                    onClick={() => toggleGroup(key)}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="font-semibold text-sm text-gray-700 dark:text-dark-text">
                        {group.teamName}
                      </span>
                      {group.isPersonal && (
                        <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          personal
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 font-medium">
                      {availableGames.length} available
                    </span>
                  </button>

                  {/* Accordion Content */}
                  {isExpanded && (
                    <div className="divide-y divide-gray-100 dark:divide-dark-border">
                      {availableGames.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-400 dark:text-dark-text-muted italic">
                          All games from this workspace are already in the team
                        </p>
                      ) : (
                        availableGames.map(game => {
                          const isSelected = selectedGameId === game.id
                          return (
                            <button
                              key={game.id}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                                isSelected
                                  ? 'bg-purple-50 dark:bg-purple-900/20'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-800/30'
                              }`}
                              onClick={() => setSelectedGameId(isSelected ? null : game.id)}
                            >
                              {/* Game Icon */}
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                {game.content?.icon_url ? (
                                  <img
                                    src={game.content.icon_url}
                                    alt={game.name}
                                    className="w-full h-full object-contain"
                                    onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                                  />
                                ) : (
                                  <Gamepad2 className="w-5 h-5 text-gray-400" />
                                )}
                              </div>

                              {/* Game Info */}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-gray-900 dark:text-dark-text truncate">
                                  {game.name}
                                </p>
                                {game.description && (
                                  <p className="text-xs text-gray-500 dark:text-dark-text-muted truncate">
                                    {game.description}
                                  </p>
                                )}
                              </div>

                              {/* Selection indicator */}
                              <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                  isSelected
                                    ? 'border-transparent'
                                    : 'border-gray-300 dark:border-gray-600'
                                }`}
                                style={isSelected ? { background: FILL_GRADIENT } : undefined}
                              >
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                            </button>
                          )
                        })
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-dark-border">
          <button
            onClick={handleClose}
            disabled={isTransferring}
            className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedGameId || isTransferring}
            className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
            style={{ background: FILL_GRADIENT }}
          >
            {isTransferring ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Adding...
              </>
            ) : (
              'Add Game'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddGameModal
